/**
 * A password the device generated for a passkey account: a passkey sign-up,
 * or an invite accepted with a passkey. Cognito needs a password; nobody types
 * this one. It is kept only until a passkey is saved or the person sets a
 * password of their own (FinishSignInGate), then deleted. While it exists,
 * FinishSignInGate holds the signed-in app until one of the two happens.
 */
// Native modules load on first use, not at import: auth actions import this
// module, and nothing that only imports them should need the keychain or the
// random source.
const secureStore = (): typeof import('expo-secure-store') => require('expo-secure-store');
const randomBytes = (count: number): Uint8Array => (require('expo-crypto') as typeof import('expo-crypto')).getRandomBytes(count);

const KEY = 'eleplan_pending_credential';

export type PendingCredential = { email: string; password?: string };

// Browser SecureStore is localStorage. Keep the password in this page only; a
// nonsecret marker survives reload so the gate can offer passkey or code recovery.
let browserCredential: PendingCredential | null = null;
const isBrowser = () => (require('react-native') as typeof import('react-native')).Platform.OS === 'web'
  && !(typeof window !== 'undefined' && (window as any).electronBridge);

/** 52 characters; the fixed ends satisfy the pool's upper/lower/digit rule. */
export function generatePasskeyAccountPassword(): string {
  const hex = Array.from(randomBytes(24), (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `Pk${hex}9z`;
}

export async function savePendingCredential(email: string, password: string): Promise<void> {
  const credential = { email: email.trim().toLowerCase(), password };
  if (isBrowser()) {
    await secureStore().setItemAsync(KEY, JSON.stringify({ email: credential.email, pending: true }));
    browserCredential = credential;
  } else {
    await secureStore().setItemAsync(KEY, JSON.stringify(credential));
  }
}

/** The pending credential, if any; only for `email` when one is given. */
export async function getPendingCredential(email?: string | null): Promise<PendingCredential | null> {
  try {
    const raw = await secureStore().getItemAsync(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (typeof value?.email !== 'string') return null;
    if (isBrowser()) {
      if (typeof value.password === 'string') {
        // Migrate a password a previous build wrote to plaintext browser storage.
        browserCredential = { email: value.email, password: value.password };
        await secureStore().setItemAsync(KEY, JSON.stringify({ email: value.email, pending: true }));
      } else if (value.pending !== true) return null;
      if (email && value.email !== email.trim().toLowerCase()) return null;
      return browserCredential?.email === value.email ? browserCredential : { email: value.email };
    }
    if (typeof value.password !== 'string') return null;
    if (email && value.email !== email.trim().toLowerCase()) return null;
    return value;
  } catch {
    return null;
  }
}

export async function clearPendingCredential(): Promise<void> {
  browserCredential = null;
  try {
    await secureStore().deleteItemAsync(KEY);
  } catch {
    // Nothing stored, or nothing to remove.
  }
}

/** An invite email's claim link (/claim?t=…), which skips the first-run tour. */
export function isClaimInviteUrl(url: string | null | undefined): boolean {
  return /^(?:https?:\/\/[^/?#]+\/|[a-z][a-z0-9+.-]*:\/\/)claim(?:[/?#]|$)/i.test(String(url || ''));
}
